import { Injectable } from '@angular/core';
import { Observable, Subscription, defer, finalize } from 'rxjs';

/** Имя общей для всех вкладок очереди обновления токена (кабинет и админка — одно приложение). */
const LOCK_NAME = 'zhmyak-auth-refresh';

/**
 * Сколько ждём очередь, мс. Больше таймаута самого запроса обновления (30 с у
 * timeoutInterceptor): держатель очереди к этому моменту гарантированно закончит —
 * успехом, ошибкой или таймаутом. Уйди мы раньше, отправили бы тот же refresh, что
 * держатель ещё обменивает, и проиграли бы гонку. Зависшая или замороженная браузером
 * вкладка дольше этого остальные не держит: дальше задача идёт без очереди, а внутри
 * неё всё равно перепроверяется общее хранилище (см. AuthService.refreshToken).
 */
const LOCK_WAIT_MS = 40_000;

/**
 * BE-1478: обновление токена по очереди между вкладками одного браузера.
 *
 * Бэк ротирует refresh-токен на каждом обновлении, а хранилище у вкладок общее: две
 * вкладки, одновременно отправившие один и тот же refresh, — это гарантированный
 * отказ одной из них. Очередь на Web Locks API пускает задачи по одной; следующая
 * вкладка, дождавшись очереди, видит уже обновлённую пару и в сеть не идёт.
 *
 * BE-1490: в ту же очередь встаёт «Выход» — пока он решает, чью сессию чистить, обновление
 * пару не сменит, а начатое обновление он дождётся и отзовёт уже новую пару.
 *
 * Без `navigator.locks` (незащищённый контекст, старые Safari/WebView) задача идёт
 * сразу — остаётся single-flight внутри вкладки и перепроверка хранилища после отказа.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshLock {
  /** Имя очереди; спеки подменяют его, чтобы тесты не делили одну очередь между собой. */
  readonly lockName: string = LOCK_NAME;

  run<T>(task: () => Observable<T>): Observable<T> {
    const locks = globalThis.navigator?.locks;
    if (!locks) return defer(task);

    return new Observable<T>((subscriber) => {
      let inner: Subscription | undefined;
      // AbortSignal.timeout есть не везде (Safari 15.4–15.x при наличии navigator.locks).
      const abort = new AbortController();
      const waitTimer = setTimeout(() => abort.abort(), LOCK_WAIT_MS);

      locks
        .request(this.lockName, { signal: abort.signal }, () => {
          clearTimeout(waitTimer);
          // Очередь держится, пока задача не завершится (успех, ошибка или отписка).
          return new Promise<void>((release) => {
            if (inner || subscriber.closed) return release();
            inner = task().pipe(finalize(release)).subscribe(subscriber);
          });
        })
        // Ожидание вышло (или API отказал) — идём без очереди.
        .catch(() => {
          if (!inner && !subscriber.closed)
            inner = task().subscribe(subscriber);
        });

      return () => {
        clearTimeout(waitTimer);
        // Отписались, не дождавшись очереди, — уходим из неё, а не ждём впустую.
        if (!inner) abort.abort();
        inner?.unsubscribe();
      };
    });
  }
}
